import axios from 'axios';
import HttpApiCallError from '@client/connectivity/HttpApiCallError';
import { endSession } from './endSession';
import { getFirebaseAuth } from './firebaseAuth';
import { HTTP_UNAUTHORIZED } from './httpStatus';

const REQUEST_TIMEOUT = 14000;

function isApiCallError(error) {
  return error?.name === 'HttpApiCallError';
}

function toApiCallError(error) {
  if (isApiCallError(error)) {
    return error;
  }

  const response = error?.response;
  return new HttpApiCallError(
    response?.statusText,
    response?.status,
    response?.data
  );
}

function withBearer(config, token) {
  return {
    ...config,
    headers: { ...config.headers, Authorization: `Bearer ${token}` },
  };
}

// Firebase errors that mean this user's Session cannot be recovered. Anything
// else (network trouble, throttling, internal errors) may pass, so it does not
// sign the user out.
const DEAD_TOKEN_ERROR_CODES = [
  'auth/user-token-expired',
  'auth/invalid-user-token',
  'auth/user-disabled',
  'auth/user-not-found',
];

function isDeadTokenError(error) {
  return DEAD_TOKEN_ERROR_CODES.includes(error?.code);
}

function getCurrentUser() {
  const auth = getFirebaseAuth();
  return auth ? auth.currentUser : null;
}

export function createPublicClient({ baseURL }) {
  return axios.create({ baseURL, timeout: REQUEST_TIMEOUT });
}

export function createPortalClient({ baseURL, portal }) {
  const client = createPublicClient({ baseURL });

  client.interceptors.request.use((config) => {
    const user = getCurrentUser();

    if (!user) {
      return Promise.reject(
        new HttpApiCallError('Sign-in required', HTTP_UNAUTHORIZED)
      );
    }

    return user.getIdToken().then((token) => withBearer(config, token));
  });

  client.interceptors.response.use(undefined, (error) => {
    if (isApiCallError(error) || error.response?.status !== HTTP_UNAUTHORIZED) {
      return Promise.reject(toApiCallError(error));
    }

    // One retry with a force-refreshed token. If that still fails to
    // authorize, the Session is over.
    const user = getCurrentUser();

    if (!user) {
      return Promise.reject(toApiCallError(error));
    }

    return user
      .getIdToken(true)
      .then((token) => axios.request(withBearer(error.config, token)))
      .catch((retryError) => {
        if (retryError?.response?.status === HTTP_UNAUTHORIZED) {
          return endSession(portal).then(() =>
            Promise.reject(toApiCallError(retryError))
          );
        }

        if (isDeadTokenError(retryError)) {
          return endSession(portal).then(() =>
            Promise.reject(
              new HttpApiCallError('Session expired', HTTP_UNAUTHORIZED)
            )
          );
        }

        return Promise.reject(toApiCallError(retryError));
      });
  });

  return client;
}
